import { React, useState, useEffect } from 'react';
import axios from 'axios';
import '../../App.css';
import {
  Grid,
  TextField,
  Box,
  Button,
  Paper,
  Stack,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
} from '@mui/material';
import { RoomPreferences, ArrowForwardIos, History } from '@mui/icons-material';
import Notification from '../Notification';
import ConfirmDialog from '../ConfirmDialog';
import PFRulesHistoryTable from '../table-data/PFRulesHistoryTable';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../common/constants';

// Custom component for required field label
const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

const getTodayDateString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const initialFValues = {
  effectiveFrom: getTodayDateString(),
  status: false,
  pfPercent: '',
  pfBasicSalaryLimit: '',
  epsPercent: '',
  epsSalaryLimit: '',
  edliPercent: '',
  edliSalaryLimit: '',
  edliMinAmt: '',
  epfAdminPercent: '',
  epfAdminMinAmt: '',
  epfInspectionPercent: '',
  epfInspectionMinAmt: '',
  edliAdminPercent: '',
  edliAdminMinAmt: '',
  edliInspectionPercent: '',
  edliInspectionMinAmt: '',
};

const tabs = ['EPF', 'EPS', 'EDLI'];

const TAB_FIELDS = {
  EPF: ['pfBasicSalaryLimit', 'pfPercent', 'epfAdminPercent', 'epfAdminMinAmt', 'epfInspectionPercent', 'epfInspectionMinAmt'],
  EPS: ['epsPercent', 'epsSalaryLimit'],
  EDLI: ['edliPercent', 'edliSalaryLimit', 'edliMinAmt', 'edliAdminPercent', 'edliAdminMinAmt', 'edliInspectionPercent', 'edliInspectionMinAmt'],
};

const NUMERIC_FIELDS = Object.values(TAB_FIELDS).flat();

const isBlank = (value) => value === '' || value === null || value === undefined;

// 0 is a valid configured value, so only null/undefined fall back to ''.
const toFieldValue = (value) => (value === null || value === undefined ? '' : value);

function PF_RulesConfiguration() {
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [currentRule, setCurrentRule] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', subTitle: '' });
  const [selectedTab, setSelectedTab] = useState('EPF'); // Default EPF
  const navigate = useNavigate();

  const getTabErrors = (tab) => {
    const temp = {};
    TAB_FIELDS[tab].forEach((field) => {
      temp[field] = isBlank(values[field]) ? 'This field is required.' : '';
    });
    return temp;
  };

  const validateTab = (tab) => {
    const temp = getTabErrors(tab);
    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === '');
  };

  const validateAll = () => {
    const temp = {
      effectiveFrom: values.effectiveFrom ? '' : 'This field is required.',
      ...getTabErrors('EPF'),
      ...getTabErrors('EPS'),
      ...getTabErrors('EDLI'),
    };
    setErrors(temp);
    const firstTabWithError = tabs.find((tab) => TAB_FIELDS[tab].some((field) => temp[field]));
    if (firstTabWithError) setSelectedTab(firstTabWithError);
    return Object.values(temp).every((x) => x === '');
  };

  const convertToDefEventPara = (name, value) => ({
    target: {
      name: name,
      value: value,
    },
  });

  function handleInputChange(e) {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  }

  const handleSessionExpired = () => {
    setNotify({
      isOpen: true,
      message: 'Session Expired!',
      type: 'error',
    });
    setTimeout(() => {
      navigate('/');
    }, 1000);
  };

  async function getPFRules() {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/configure/pf-rule-configure/get`
      );
      const current = response.data.current || null;
      setCurrentRule(current);
      setHistory(response.data.history || []);

      const rule = current || {};
      setValues({
        ...initialFValues,
        effectiveFrom: getTodayDateString(),
        status: Boolean(rule.PfStatus),
        pfBasicSalaryLimit: toFieldValue(rule.PfBasicLimit),
        pfPercent: toFieldValue(rule.PfBasicPer),
        epfAdminPercent: toFieldValue(rule.epfAdminPercent),
        epfAdminMinAmt: toFieldValue(rule.epfAdminMinAmt),
        epfInspectionPercent: toFieldValue(rule.epfInspectionPercent),
        epfInspectionMinAmt: toFieldValue(rule.epfInspectionMinAmt),
        epsPercent: toFieldValue(rule.epsPercent),
        epsSalaryLimit: toFieldValue(rule.epsSalaryLimit),
        edliPercent: toFieldValue(rule.edliPercent),
        edliSalaryLimit: toFieldValue(rule.edliSalaryLimit),
        edliMinAmt: toFieldValue(rule.edliMinAmt),
        edliAdminPercent: toFieldValue(rule.edliAdminPercent),
        edliAdminMinAmt: toFieldValue(rule.edliAdminMinAmt),
        edliInspectionPercent: toFieldValue(rule.edliInspectionPercent),
        edliInspectionMinAmt: toFieldValue(rule.edliInspectionMinAmt),
      });
    } catch (e) {
      if (e.response?.status === 401) {
        handleSessionExpired();
      } else {
        setNotify({
          isOpen: true,
          message: e.response?.data?.error_message || 'Failed to load PF rules',
          type: 'error',
        });
      }
    }
  }

  useEffect(() => {
    getPFRules();
  }, []);

  const handleNext = () => {
    // Only proceed to the next tab if validation passes for the current tab
    if (validateTab(selectedTab)) {
      const currentIndex = tabs.indexOf(selectedTab);
      if (currentIndex < tabs.length - 1) {
        setSelectedTab(tabs[currentIndex + 1]);
      }
    }
  };

  const closeConfirmDialog = () => setConfirmDialog((prev) => ({ ...prev, isOpen: false }));

  const submitRules = (confirmProcessedPayroll) => {
    const payload = {
      effectiveFrom: values.effectiveFrom,
      status: values.status,
      confirmProcessedPayroll,
    };
    NUMERIC_FIELDS.forEach((field) => {
      payload[field] = parseFloat(values[field]);
    });

    setSaving(true);
    axios
      .post(`${process.env.REACT_APP_API_BASE_URL}/configure/pf-rule-configure/save`, payload)
      .then((response) => {
        setNotify({
          isOpen: true,
          message: response.data.message,
          type: 'success',
        });
        setSelectedTab('EPF');
        setErrors({});
        getPFRules();
      })
      .catch((e) => {
        const status = e.response?.status;
        if (status === 401) {
          handleSessionExpired();
        } else if (status === 409) {
          const months = e.response.data.processedMonths || [];
          setConfirmDialog({
            isOpen: true,
            title: 'Payroll already processed for these months',
            subTitle: `${months.join(', ')}. Re-processing any of these months will use the new PF rules. Save anyway?`,
            onConfirm: () => {
              closeConfirmDialog();
              submitRules(true);
            },
          });
        } else {
          setNotify({
            isOpen: true,
            message: e.response?.data?.error_message || 'Failed to save PF rules',
            type: 'error',
          });
        }
      })
      .finally(() => setSaving(false));
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!validateAll()) return;

    if (history.some((rule) => rule.EffectiveFrom === values.effectiveFrom)) {
      setConfirmDialog({
        isOpen: true,
        title: `PF rules effective from ${formatDate(values.effectiveFrom)} already exist`,
        subTitle: 'Saving will replace the values of that version. Continue?',
        onConfirm: () => {
          closeConfirmDialog();
          submitRules(false);
        },
      });
      return;
    }
    submitRules(false);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', margin: '10px', padding: '20px', fontWeight: '500px' }}>
      <Paper elevation={5} sx={{ width: '95%', maxWidth: '1400px', padding: '20px' }}>
        <Grid className="form-title" container spacing={2} justifyContent="center" alignItems="center">
          <Grid item>
            <RoomPreferences sx={{ width: 45, height: 45 }} />
          </Grid>
          <Grid item>
            <Typography variant="h5" sx={{ textAlign: 'center' }}>
              PF Rules Configuration
            </Typography>
          </Grid>
        </Grid>

        <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: 1 }}>
          {currentRule
            ? `Current rules effective from ${formatDate(currentRule.EffectiveFrom)}. Saving with a new Effective From date creates a new version; older versions are kept below.`
            : 'No PF rules in effect yet. Saving will create the first version.'}
        </Typography>

        {/* Effective From applies to the whole version (EPF, EPS and EDLI) */}
        <Grid container justifyContent="center" sx={{ mt: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              variant="outlined"
              className="date-picker"
              type="date"
              label={<RequiredLabel label="Effective From" />}
              name="effectiveFrom"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={values.effectiveFrom}
              onChange={handleInputChange}
              helperText={
                errors.effectiveFrom ||
                (values.effectiveFrom && !values.effectiveFrom.endsWith('-01')
                  ? "Not the 1st of a month: that month's PF will be split day-wise between the previous rules and these."
                  : '')
              }
              {...(errors.effectiveFrom && { error: true })}
            />
          </Grid>
        </Grid>

        {/* Tiles for EPF, EPS, EDLI (requested sequence) with smaller size */}
        <Grid container spacing={1} justifyContent="center" sx={{ mb: 3, mt: 3 }}>
          {tabs.map((tab) => (
            <Grid item key={tab}>
              <Paper
                elevation={selectedTab === tab ? 6 : 2}
                sx={{
                  p: 1.2,
                  cursor: 'pointer',
                  textAlign: 'center',
                  backgroundColor: selectedTab === tab ? 'primary.main' : 'grey.100',
                  color: selectedTab === tab ? 'white' : 'black',
                  width: 90,
                  userSelect: 'none',
                  borderRadius: 2,
                  fontWeight: '600',
                }}
                onClick={() => {
                  setSelectedTab(tab);
                  setErrors({});
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>
                  {tab}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Form sections based on selectedTab */}
        <form className="classes.root" autoComplete="off">
          {selectedTab === 'EPF' && (
            <Grid container spacing={2} sx={{ marginTop: '10px' }}>
              {/* EPF Basic */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ padding: '15px', height: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      marginBottom: '15px',
                      gap: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="status"
                          color="primary"
                          checked={values.status}
                          onChange={(e) => handleInputChange(convertToDefEventPara('status', e.target.checked))}
                        />
                      }
                      label="Active"
                      sx={{ color: 'green', marginRight: 2 }}
                    />
                    <hr style={{ width: '50px', color: 'green', margin: 0 }} />
                    <Typography sx={{ color: 'green', fontWeight: 'bold', whiteSpace: 'nowrap', marginX: 1 }}>
                      EPF
                    </Typography>
                    <hr style={{ width: '50px', color: 'green', margin: 0 }} />
                  </Box>
                  <Stack spacing={2}>
                    <TextField
                      variant="outlined"
                      name="pfBasicSalaryLimit"
                      label={<RequiredLabel label="PF Salary Limit (₹)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.pfBasicSalaryLimit}
                      onChange={handleInputChange}
                      {...(errors.pfBasicSalaryLimit && { error: true, helperText: errors.pfBasicSalaryLimit })}
                    />
                    <TextField
                      variant="outlined"
                      name="pfPercent"
                      label={<RequiredLabel label="PF (%)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.pfPercent}
                      onChange={handleInputChange}
                      {...(errors.pfPercent && { error: true, helperText: errors.pfPercent })}
                    />
                  </Stack>
                </Paper>
              </Grid>
              {/* EPF Admin */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ padding: '15px', height: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
                    <hr style={{ width: '50px', color: 'green' }} />
                    <p style={{ marginLeft: '10px', marginRight: '10px', color: 'green', fontWeight: 'bold' }}>
                      EPF Admin
                    </p>
                    <hr style={{ width: '50px', color: 'green' }} />
                  </div>
                  <Stack spacing={2}>
                    <TextField
                      variant="outlined"
                      name="epfAdminMinAmt"
                      label={<RequiredLabel label="EPF Admin Min Amt (₹)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.epfAdminMinAmt}
                      onChange={handleInputChange}
                      {...(errors.epfAdminMinAmt && { error: true, helperText: errors.epfAdminMinAmt })}
                    />
                    <TextField
                      variant="outlined"
                      name="epfAdminPercent"
                      label={<RequiredLabel label="EPF Admin (%)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.epfAdminPercent}
                      onChange={handleInputChange}
                      {...(errors.epfAdminPercent && { error: true, helperText: errors.epfAdminPercent })}
                    />
                  </Stack>
                </Paper>
              </Grid>
              {/* EPF Inspection */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ padding: '15px', height: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
                    <hr style={{ width: '50px', color: 'green' }} />
                    <p style={{ marginLeft: '10px', marginRight: '10px', color: 'green', fontWeight: 'bold' }}>
                      EPF Inspection
                    </p>
                    <hr style={{ width: '50px', color: 'green' }} />
                  </div>
                  <Stack spacing={2}>
                    <TextField
                      variant="outlined"
                      name="epfInspectionMinAmt"
                      label={<RequiredLabel label="EPF Insp Min Amt (₹)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.epfInspectionMinAmt}
                      onChange={handleInputChange}
                      {...(errors.epfInspectionMinAmt && { error: true, helperText: errors.epfInspectionMinAmt })}
                    />
                    <TextField
                      variant="outlined"
                      name="epfInspectionPercent"
                      label={<RequiredLabel label="EPF Insp (%)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.epfInspectionPercent}
                      onChange={handleInputChange}
                      {...(errors.epfInspectionPercent && { error: true, helperText: errors.epfInspectionPercent })}
                    />
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}

          {selectedTab === 'EPS' && (
            <Grid container spacing={2} sx={{ marginTop: '10px' }}>
              <Grid item xs={12} md={6} sx={{ mx: 'auto' }}>
                <Paper elevation={2} sx={{ padding: '15px', height: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
                    <hr style={{ width: '50px', color: 'green' }} />
                    <p style={{ marginLeft: '10px', marginRight: '10px', color: 'green', fontWeight: 'bold' }}>
                      EPS
                    </p>
                    <hr style={{ width: '50px', color: 'green' }} />
                  </div>
                  <Stack spacing={2}>
                    <TextField
                      variant="outlined"
                      name="epsSalaryLimit"
                      label={<RequiredLabel label="EPS Salary Limit (₹)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.epsSalaryLimit}
                      onChange={handleInputChange}
                      {...(errors.epsSalaryLimit && { error: true, helperText: errors.epsSalaryLimit })}
                    />
                    <TextField
                      variant="outlined"
                      name="epsPercent"
                      label={<RequiredLabel label="EPS (%)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.epsPercent}
                      onChange={handleInputChange}
                      {...(errors.epsPercent && { error: true, helperText: errors.epsPercent })}
                    />
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}

          {selectedTab === 'EDLI' && (
            <Grid container spacing={2} sx={{ marginTop: '10px' }}>
              {/* EDLI Basic */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ padding: '15px', height: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
                    <hr style={{ width: '50px', color: 'green' }} />
                    <p style={{ marginLeft: '10px', marginRight: '10px', color: 'green', fontWeight: 'bold' }}>
                      EDLI
                    </p>
                    <hr style={{ width: '50px', color: 'green' }} />
                  </div>
                  <Stack spacing={2}>
                    <TextField
                      variant="outlined"
                      name="edliSalaryLimit"
                      label={<RequiredLabel label="EDLI Salary Limit (₹)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.edliSalaryLimit}
                      onChange={handleInputChange}
                      {...(errors.edliSalaryLimit && { error: true, helperText: errors.edliSalaryLimit })}
                    />
                    <TextField
                      variant="outlined"
                      name="edliPercent"
                      label={<RequiredLabel label="EDLI (%)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.edliPercent}
                      onChange={handleInputChange}
                      {...(errors.edliPercent && { error: true, helperText: errors.edliPercent })}
                    />
                    <TextField
                      variant="outlined"
                      name="edliMinAmt"
                      label={<RequiredLabel label="EDLI Min Amount (₹)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.edliMinAmt}
                      onChange={handleInputChange}
                      {...(errors.edliMinAmt && { error: true, helperText: errors.edliMinAmt })}
                    />
                  </Stack>
                </Paper>
              </Grid>
              {/* EDLI Admin */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ padding: '15px', height: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
                    <hr style={{ width: '50px', color: 'green' }} />
                    <p style={{ marginLeft: '10px', marginRight: '10px', color: 'green', fontWeight: 'bold' }}>
                      EDLI Admin
                    </p>
                    <hr style={{ width: '50px', color: 'green' }} />
                  </div>
                  <Stack spacing={2}>
                    <TextField
                      variant="outlined"
                      name="edliAdminMinAmt"
                      label={<RequiredLabel label="EDLI Admin Min Amt (₹)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.edliAdminMinAmt}
                      onChange={handleInputChange}
                      {...(errors.edliAdminMinAmt && { error: true, helperText: errors.edliAdminMinAmt })}
                    />
                    <TextField
                      variant="outlined"
                      name="edliAdminPercent"
                      label={<RequiredLabel label="EDLI Admin (%)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.edliAdminPercent}
                      onChange={handleInputChange}
                      {...(errors.edliAdminPercent && { error: true, helperText: errors.edliAdminPercent })}
                    />
                  </Stack>
                </Paper>
              </Grid>
              {/* EDLI Inspection */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ padding: '15px', height: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
                    <hr style={{ width: '50px', color: 'green' }} />
                    <p style={{ marginLeft: '10px', marginRight: '10px', color: 'green', fontWeight: 'bold' }}>
                      EDLI Inspection
                    </p>
                    <hr style={{ width: '50px', color: 'green' }} />
                  </div>
                  <Stack spacing={2}>
                    <TextField
                      variant="outlined"
                      name="edliInspectionMinAmt"
                      label={<RequiredLabel label="EDLI Insp Min Amt (₹)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.edliInspectionMinAmt}
                      onChange={handleInputChange}
                      {...(errors.edliInspectionMinAmt && { error: true, helperText: errors.edliInspectionMinAmt })}
                    />
                    <TextField
                      variant="outlined"
                      name="edliInspectionPercent"
                      label={<RequiredLabel label="EDLI Insp (%)" />}
                      size="small"
                      type="number"
                      fullWidth
                      value={values.edliInspectionPercent}
                      onChange={handleInputChange}
                      {...(errors.edliInspectionPercent && { error: true, helperText: errors.edliInspectionPercent })}
                    />
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Submit/Next Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIos />}
              disabled={saving}
              size="large"
              onClick={selectedTab === 'EDLI' ? handleSubmit : handleNext}
            >
              {selectedTab === 'EDLI' ? 'Save' : 'Next'}
            </Button>
          </Box>
        </form>

        <Divider sx={{ my: 4 }} />

        <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <History sx={{ width: 32, height: 32 }} />
          </Grid>
          <Grid item>
            <Typography variant="h6">PF Rules History</Typography>
          </Grid>
        </Grid>
        <PFRulesHistoryTable history={history} currentRule={currentRule} />
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </Box>
  );
}

export default PF_RulesConfiguration;
